import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Badge, Button, TextInput } from 'components/common'
import { Switch } from 'components/common/switch'
import Modal from 'components/modal'
import { CronJob, CronJobOwner } from 'types/cronJob'
import { updateCronJob } from 'utils/api/cronJobsApi'
import { formatDate } from 'utils/helpers/date'
import { handleError } from 'utils/helpers/handleError'
import {
  formatDuration,
  formatMinutes,
  HEALTH_INFO,
  isManualOnly,
} from './utils'

type Props = {
  job?: CronJob
  isEditor: boolean
  closeModal: () => void
  onDone: () => void
}

type FormValues = {
  expression: string
  maxRunMinutes: string
  maxGapMinutes: string
}

const showDate = (date?: string) =>
  date ? formatDate(date, { includeTime: true }) : '-'

const showOwner = (owner?: CronJobOwner) =>
  owner
    ? `${owner.appName}#${owner.pm2Id ?? '-'} (${owner.host}, pid ${owner.pid})`
    : '-'

// Blank -> null (clear the override), otherwise the number of minutes
const toOverride = (value: string) => (value.trim() ? Number(value) : null)

// One read-only label/value pair in the details grid
const Detail = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase text-gray-400">{label}</p>
    <div className="break-words text-sm">{children}</div>
  </div>
)

// View a job's details; editors can also change its schedule and limits
const EditCronJobModal = ({ job, isEditor, closeModal, onDone }: Props) => {
  const [isActive, setIsActive] = useState(job?.isActive !== false)
  const [loading, setLoading] = useState(false)

  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      expression: job?.expression ?? '',
      maxRunMinutes: job?.maxRunMinutesOverride?.toString() ?? '',
      maxGapMinutes: job?.maxGapMinutesOverride?.toString() ?? '',
    },
  })

  if (!job) return null

  const jobLabel = job.title ?? job._id
  const hasSchedule = !isManualOnly(job)
  const isRunning = job.status === 'running'

  // Sends only what changed; nothing changed just closes the popup
  const save = async (values: FormValues) => {
    const expression = values.expression.trim()
    const maxRun = toOverride(values.maxRunMinutes)
    const maxGap = toOverride(values.maxGapMinutes)

    // NaN would be sent as null and silently clear the override
    const isBadLimit = (value: number | null) =>
      value !== null && !(Number.isInteger(value) && value >= 1)

    if (isBadLimit(maxRun) || isBadLimit(maxGap)) {
      toast.error('Max Run and Max Gap must be whole minutes (1 or more)')
      return
    }

    const changes = {
      ...(hasSchedule && expression !== job.expression && { expression }),
      ...(isActive !== (job.isActive !== false) && { isActive }),
      ...(hasSchedule &&
        maxRun !== (job.maxRunMinutesOverride ?? null) && {
          maxRunMinutes: maxRun,
        }),
      ...(hasSchedule &&
        maxGap !== (job.maxGapMinutesOverride ?? null) && {
          maxGapMinutes: maxGap,
        }),
    }

    if (!Object.keys(changes).length) return closeModal()

    setLoading(true)
    const toastId = toast.loading(`Saving ${jobLabel}...`)

    try {
      const { data } = await updateCronJob(job._id, changes)
      toast.success(data?.message ?? 'Cron job updated', { id: toastId })
      closeModal()
      onDone()
    } catch (error) {
      handleError(error, toastId)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      width={720}
      title={jobLabel}
      titleClassname="text-xl font-semibold max-w-2xl"
      isOpen
      closeModal={closeModal}
      customStyle={{ padding: '25px 32px' }}
    >
      <form onSubmit={handleSubmit(save)} className="space-y-5">
        {/* Who the job is, and its on/off switch */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <code>{job._id}</code>
              {job.group && <Badge color="gray">{job.group}</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">
                {isActive ? 'Active' : 'Inactive'}
              </span>
              <Switch
                checked={isActive}
                onChange={setIsActive}
                disabled={!isEditor}
              />
            </div>
          </div>
          {job.description && (
            <p className="mt-2 text-sm text-gray-600">{job.description}</p>
          )}
        </div>

        {/* Current state, read only */}
        <div className="grid gap-x-6 gap-y-3 rounded-lg border bg-gray-50 p-4 sm:grid-cols-3">
          <Detail label="Status">
            <span className="flex items-center gap-1.5">
              <Badge color={HEALTH_INFO[job.health].color}>
                {HEALTH_INFO[job.health].label}
              </Badge>
              <Badge color={isRunning ? 'blue' : 'gray'}>
                {isRunning
                  ? `Running ${formatMinutes(job.runningMinutes)}`
                  : 'Idle'}
              </Badge>
            </span>
          </Detail>
          <Detail label="Last success">{showDate(job.lastSuccessAt)}</Detail>
          <Detail label="Last run took">
            {formatDuration(job.lastDurationMs)}
          </Detail>
          <Detail label="Last finished">{showDate(job.lastFinishedAt)}</Detail>
          <Detail label={isRunning ? 'Running on' : 'Last ran on'}>
            {showOwner(isRunning ? job.runningOn : job.lastRunOn)}
          </Detail>
          <Detail label="Last failure">{showDate(job.lastFailureAt)}</Detail>
          {job.inactiveReason && (
            <div className="sm:col-span-3">
              <Detail label="Inactive reason">{job.inactiveReason}</Detail>
            </div>
          )}
          {(job.lastError || job.setupError?.message) && (
            <div className="sm:col-span-3">
              <Detail label="Last error">
                <span className="text-red-600">
                  {job.setupError?.message ?? job.lastError}
                </span>
              </Detail>
            </div>
          )}
        </div>

        {/* Settings: editable for editors */}
        <div className="space-y-4">
          <p className="text-sm font-bold text-sidebar-primary">Settings</p>

          {hasSchedule ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextInput
                  required
                  register={register}
                  name="expression"
                  label="Cron Expression"
                  placeholder="e.g., 0 9,13,17 * * *"
                  hintText="Asia/Dubai time"
                  disabled={!isEditor}
                />
                <TextInput
                  register={register}
                  type="number"
                  name="maxRunMinutes"
                  label="Max Run (minutes)"
                  placeholder={`Auto: ${job.maxRunMinutes ?? '-'}`}
                  hintText="Alert when a run goes longer. Blank = auto"
                  disabled={!isEditor}
                />
                <TextInput
                  register={register}
                  type="number"
                  name="maxGapMinutes"
                  label="Max Gap (minutes)"
                  placeholder={`Auto: ${job.maxGapMinutes ?? '-'}`}
                  hintText="Overdue alert limit. Blank = auto"
                  disabled={!isEditor}
                />
              </div>
              <div className="space-y-1 rounded-md bg-gray-50 p-3 text-[11px] text-gray-500">
                <p>
                  <span className="font-semibold">Max Run:</span> if a single
                  run is still going after this many minutes, a &quot;Running
                  long&quot; alert is sent. The run is not stopped.
                </p>
                <p>
                  <span className="font-semibold">Max Gap:</span> the longest
                  the job may go without a successful run. Counted from the last
                  success (or from the last restart / expression change, if
                  later). Once it passes, the job shows as Overdue and an alert
                  is sent once. A success clears it. Example: a job running
                  every 30 min with Max Gap 75 can miss one run, but alerts if
                  it misses two in a row or keeps failing.
                </p>
                <p>
                  Leave blank to use the auto value worked out from the schedule
                  (Max Run = time between runs, Max Gap = twice that plus a
                  small buffer).
                </p>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              This job has no schedule; it runs only from its manual trigger.
            </p>
          )}
        </div>

        <div className="flex w-full items-center gap-5">
          {isEditor && (
            <Button
              className="w-full"
              buttonText="Save"
              type="submit"
              loading={loading}
            />
          )}
          <Button
            className="w-full"
            buttonText={isEditor ? 'Cancel' : 'Close'}
            variant="outline"
            type="button"
            onClick={closeModal}
          />
        </div>
      </form>
    </Modal>
  )
}

export default EditCronJobModal
